import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import type { ApiError } from '@iheartjane/data-access';
import { trackError } from '@iheartjane/tracking';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router';

import { getJwt } from '@jane/shared/auth';
import { useSafeEcommApp } from '@jane/shared/providers';

interface UseMultifactorAuthenticationProps {
  newPhone?: string;
  onCodeResent?: () => void;
  onCodeSentSuccess?: () => void;
  onVerifySuccess?: () => void;
}

/**
 * This hook handles all the logic for sending and resending mfa codes.
 * When used in tandem with the MfaProvider and useMfa, you can utilize a single MfaModal
 * that tracks codes being sent and mfa being completed.
 */

export const useMultifactorAuthentication = ({
  newPhone,
  onCodeSentSuccess,
  onCodeResent,
  onVerifySuccess,
}: UseMultifactorAuthenticationProps) => {
  const { appMode } = useSafeEcommApp();
  const { pathname } = useLocation();
  const isKiosk = appMode === 'kiosk' && !pathname.includes('mobile');
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [errorSendingCode, setErrorSendingCode] = useState<null | string>(null);
  const [errorVerifyingCode, setErrorVerifyingCode] = useState<null | string>(
    null
  );

  const sendCode = useCallback(async () => {
    setSendingCode(true);
    setErrorSendingCode(null);
    setErrorVerifyingCode(null);

    try {
      await sendMfaCode({ jwt: getJwt(), newPhone, isKiosk });
      setCodeSent(true);
      onCodeSentSuccess && onCodeSentSuccess();
    } catch (e) {
      const message = (e as ApiError).message;
      setErrorSendingCode(message);
      trackError(message);
    }

    setSendingCode(false);
  }, [isKiosk, newPhone, onCodeSentSuccess]);

  const resendCode = useCallback(() => {
    sendCode();
    setCodeResent(true);
    onCodeResent && onCodeResent();
  }, [sendCode, onCodeResent]);

  const verifyCode = useCallback(
    async (code: string) => {
      setErrorVerifyingCode(null);
      setVerifyingCode(true);

      try {
        const response = await verifyMfaCode({ code, isKiosk, jwt: getJwt() });

        if (response.success) {
          onVerifySuccess && onVerifySuccess();
        } else {
          setErrorVerifyingCode(
            response.error
              ? 'There was an error processing this request. Please try again.'
              : 'This code is incorrect, check your code and try again.'
          );
        }
      } catch {
        setErrorVerifyingCode('Something went wrong. Please try again.');
      }

      setVerifyingCode(false);
    },
    [isKiosk, onVerifySuccess]
  );

  return {
    codeResent,
    codeSent,
    errorSendingCode,
    errorVerifyingCode,
    sendCode,
    resendCode,
    sendingCode,
    verifyCode,
    verifyingCode,
  };
};
